import React, { useState, useEffect } from 'react';
import { useI18n } from '../contexts/I18nContext';
import { 
  getOrganizations, createOrganization, updateOrganization, getPlans, getFeatures, 
  toggleOrgFeature, approveOrganization, rejectOrganization, deactivateOrganization, reactivateOrganization,
  getContactLeads, deleteContactLead, updateLeadStatus
} from '../lib/api';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Building2, Plus, Settings, Shield, 
  Package, Users, DollarSign, FileSpreadsheet,
  Eye, Layers, Calendar, FileText, BarChart3, RefreshCw,
  Clock, CheckCircle, XCircle, AlertTriangle, Power, PowerOff, Mail, Trash2, Phone
} from 'lucide-react';

// Map feature codes to icons
const FEATURE_ICONS = {
  shipments: Package,
  containers: Layers,
  events: Calendar,
  internal_notes: FileText,
  dashboard: BarChart3,
  client_portal: Eye,
  notion_sync: RefreshCw,
  cost_breakdown: DollarSign,
  excel_export: FileSpreadsheet,
  multi_user: Users,
};

// Deactivation reasons - use translation function
const getDeactivationReasons = (t) => [
  { code: 'payment_pending', label: t('organizations.paymentPending'), description: t('organizations.paymentPendingDesc') },
  { code: 'payment_overdue', label: t('organizations.paymentOverdue'), description: t('organizations.paymentOverdueDesc') },
  { code: 'contract_ended', label: t('organizations.contractEnded'), description: t('organizations.contractEndedDesc') },
  { code: 'requested', label: t('organizations.clientRequested'), description: t('organizations.clientRequestedDesc') },
  { code: 'other', label: t('organizations.other'), description: t('organizations.otherDesc') },
];

export default function OrganizationsPage() {
  const { t } = useI18n();
  const [organizations, setOrganizations] = useState([]);
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgToDeactivate, setOrgToDeactivate] = useState(null);
  const [deactivateForm, setDeactivateForm] = useState({ reason: 'payment_pending', notes: '' });
  const [newOrgForm, setNewOrgForm] = useState({
    name: '',
    plan_code: 'BASIC',
    contact_email: '',
    contact_phone: '',
  });
  const [activeTab, setActiveTab] = useState('organizations');
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'leads') {
      loadLeads();
    }
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [orgsRes, plansRes, featuresRes] = await Promise.all([
        getOrganizations(),
        getPlans(),
        getFeatures(),
      ]);
      setOrganizations(orgsRes.data);
      setPlans(plansRes.data);
      setFeatures(featuresRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error(t('organizations.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadLeads = async () => {
    setLeadsLoading(true);
    try {
      const res = await getContactLeads();
      setLeads(res.data);
    } catch (err) {
      console.error('Error loading leads:', err);
    } finally {
      setLeadsLoading(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('¿Eliminar este lead?')) return;
    try {
      await deleteContactLead(leadId);
      toast.success(t('organizations.leadDeleted'));
      loadLeads();
    } catch (err) {
      toast.error(t('organizations.deleteError'));
    }
  };

  const handleUpdateLeadStatus = async (leadId, newStatus) => {
    try {
      await updateLeadStatus(leadId, newStatus);
      toast.success(t('organizations.statusUpdated'));
      loadLeads();
    } catch (err) {
      toast.error(t('organizations.statusError'));
    }
  };

  const handleCreateOrg = async () => {
    if (!newOrgForm.name.trim()) {
      toast.error(t('common.nameRequired'));
      return;
    }
    try {
      await createOrganization(newOrgForm);
      toast.success('Organización creada');
      setShowAddDialog(false);
      setNewOrgForm({ name: '', plan_code: 'BASIC', contact_email: '', contact_phone: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('organizations.statusError'));
    }
  };

  const handlePlanChange = async (orgId, newPlanCode) => {
    try {
      await updateOrganization(orgId, { plan_code: newPlanCode });
      toast.success('Plan actualizado');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('organizations.statusError'));
    }
  };

  const handleFeatureToggle = async (orgId, featureCode, currentValue) => {
    try {
      await toggleOrgFeature(orgId, featureCode, !currentValue);
      toast.success(`Feature ${!currentValue ? 'activada' : 'desactivada'}`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('organizations.statusError'));
    }
  };

  const openDeactivateDialog = (org) => {
    setOrgToDeactivate(org);
    setDeactivateForm({ reason: 'payment_pending', notes: '' });
    setShowDeactivateDialog(true);
  };

  const handleDeactivate = async () => {
    if (!orgToDeactivate) return;
    try {
      await deactivateOrganization(orgToDeactivate.id, deactivateForm.reason, deactivateForm.notes);
      toast.success('Organización desactivada');
      setShowDeactivateDialog(false);
      setOrgToDeactivate(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('organizations.statusError'));
    }
  };

  const handleReactivate = async (org) => {
    if (!window.confirm(`¿Reactivar la organización "${org.name}"? Todos los usuarios serán reactivados.`)) return;
    try {
      await reactivateOrganization(org.id);
      toast.success('Organización reactivada');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('organizations.statusError'));
    }
  };

  const handleApprove = async (org) => {
    if (!window.confirm(`¿Aprobar la organización "${org.name}"?`)) return;
    try {
      await approveOrganization(org.id);
      toast.success('Organización aprobada');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('organizations.statusError'));
    }
  };

  const handleReject = async (org) => {
    if (!window.confirm(`¿Rechazar la organización "${org.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await rejectOrganization(org.id);
      toast.success('Organización rechazada');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('organizations.statusError'));
    }
  };

  const getPlanBadgeColor = (planCode) => {
    switch (planCode) {
      case 'ENTERPRISE': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'PRO': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getReasonLabel = (reason) => {
    const found = getDeactivationReasons(t).find(r => r.code === reason);
    return found ? found.label : reason;
  };

  // Separate organizations by status
  const pendingOrgs = organizations.filter(o => o.status === 'pending');
  const deactivatedOrgs = organizations.filter(o => !o.is_active && o.status !== 'pending');
  const activeOrgs = organizations.filter(o => o.is_active && o.status !== 'pending');

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Cargando...</div>
        </div>
      </DashboardLayout>
    );
  }

  const getLeadStatusBadge = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500/20 text-blue-400';
      case 'contacted': return 'bg-yellow-500/20 text-yellow-400';
      case 'converted': return 'bg-green-500/20 text-green-400';
      case 'rejected': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="organizations-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl flex items-center gap-3">
              <Shield className="w-8 h-8 text-primary" />
              Admin Maestro
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestiona organizaciones, planes y leads de contacto
            </p>
          </div>
          {activeTab === 'organizations' && (
            <Button onClick={() => setShowAddDialog(true)} data-testid="add-org-btn">
              <Plus size={16} className="mr-2" />
              Nueva Organización
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border">
          <button
            onClick={() => setActiveTab('organizations')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'organizations' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Building2 size={16} className="inline mr-2" />
            Organizaciones ({organizations.length})
          </button>
          <button
            onClick={() => setActiveTab('leads')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'leads' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Mail size={16} className="inline mr-2" />
            Leads de Contacto {leads.filter(l => l.status === 'new').length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-primary text-primary-foreground">
                {leads.filter(l => l.status === 'new').length}
              </span>
            )}
          </button>
        </div>

        {/* Leads Tab Content */}
        {activeTab === 'leads' && (
          <div className="bg-card border border-border rounded-sm">
            {leadsLoading ? (
              <div className="p-8 text-center text-muted-foreground">Cargando leads...</div>
            ) : leads.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No hay leads de contacto</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border bg-secondary/30">
                    <th className="py-2 px-3 font-medium">Empresa</th>
                    <th className="py-2 px-3 font-medium">Contacto</th>
                    <th className="py-2 px-3 font-medium">Email</th>
                    <th className="py-2 px-3 font-medium">Teléfono</th>
                    <th className="py-2 px-3 font-medium">Mensaje</th>
                    <th className="py-2 px-3 font-medium">Estado</th>
                    <th className="py-2 px-3 font-medium">Fecha</th>
                    <th className="py-2 px-3 font-medium w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id} className="border-b border-border/50 hover:bg-secondary/30">
                      <td className="py-2 px-3 font-medium">{lead.company_name}</td>
                      <td className="py-2 px-3">{lead.contact_name}</td>
                      <td className="py-2 px-3">
                        <a href={`mailto:${lead.email}`} className="text-primary hover:underline">
                          {lead.email}
                        </a>
                      </td>
                      <td className="py-2 px-3">
                        {lead.phone ? (
                          <a href={`tel:${lead.phone}`} className="text-primary hover:underline flex items-center gap-1">
                            <Phone size={12} />
                            {lead.phone}
                          </a>
                        ) : '—'}
                      </td>
                      <td className="py-2 px-3 text-sm text-muted-foreground max-w-xs truncate">
                        {lead.message || '—'}
                      </td>
                      <td className="py-2 px-3">
                        <select
                          value={lead.status}
                          onChange={(e) => handleUpdateLeadStatus(lead.id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded border-0 cursor-pointer ${getLeadStatusBadge(lead.status)}`}
                        >
                          <option value="new">Nuevo</option>
                          <option value="contacted">Contactado</option>
                          <option value="converted">Convertido</option>
                          <option value="rejected">Rechazado</option>
                        </select>
                      </td>
                      <td className="py-2 px-3 text-sm text-muted-foreground">
                        {new Date(lead.created_at).toLocaleDateString('es-MX')}
                      </td>
                      <td className="py-2 px-3">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteLead(lead.id)}
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Organizations Tab Content */}
        {activeTab === 'organizations' && (
          <>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="text-2xl font-bold">{organizations.length}</div>
            <div className="text-sm text-muted-foreground">Total</div>
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="text-2xl font-bold text-yellow-400">{pendingOrgs.length}</div>
            <div className="text-sm text-muted-foreground">Pendientes</div>
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="text-2xl font-bold text-green-400">{activeOrgs.length}</div>
            <div className="text-sm text-muted-foreground">Activas</div>
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="text-2xl font-bold text-red-400">{deactivatedOrgs.length}</div>
            <div className="text-sm text-muted-foreground">Desactivadas</div>
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="text-2xl font-bold text-purple-400">
              {organizations.filter(o => o.plan_code === 'PRO' || o.plan_code === 'ENTERPRISE').length}
            </div>
            <div className="text-sm text-muted-foreground">Planes de Pago</div>
          </div>
        </div>

        {/* Pending Organizations - Priority section */}
        {pendingOrgs.length > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm overflow-hidden">
            <div className="bg-yellow-500/20 px-4 py-3 border-b border-yellow-500/30 flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              <h2 className="font-heading text-lg text-yellow-400">Solicitudes Pendientes ({pendingOrgs.length})</h2>
            </div>
            <div className="divide-y divide-yellow-500/20">
              {pendingOrgs.map(org => (
                <div key={org.id} className="p-4" data-testid={`pending-org-${org.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-sm bg-yellow-500/20 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-yellow-400" />
                      </div>
                      <div>
                        <h3 className="font-medium">{org.name}</h3>
                        <p className="text-sm text-muted-foreground">{org.contact_email}</p>
                        <p className="text-xs text-muted-foreground">
                          Plan solicitado: <span className="font-medium">{org.plan_name || org.plan_code}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-400 border-red-400/50 hover:bg-red-500/10"
                        onClick={() => handleReject(org)}
                        data-testid={`reject-${org.id}`}
                      >
                        <XCircle size={14} className="mr-1" />
                        Rechazar
                      </Button>
                      <Button 
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleApprove(org)}
                        data-testid={`approve-${org.id}`}
                      >
                        <CheckCircle size={14} className="mr-1" />
                        Aprobar
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Deactivated Organizations - Warning section */}
        {deactivatedOrgs.length > 0 && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-sm overflow-hidden">
            <div className="bg-red-500/20 px-4 py-3 border-b border-red-500/30 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <h2 className="font-heading text-lg text-red-400">Cuentas Desactivadas ({deactivatedOrgs.length})</h2>
            </div>
            <div className="divide-y divide-red-500/20">
              {deactivatedOrgs.map(org => (
                <div key={org.id} className="p-4" data-testid={`deactivated-org-${org.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-sm bg-red-500/20 flex items-center justify-center">
                        <PowerOff className="w-5 h-5 text-red-400" />
                      </div>
                      <div>
                        <h3 className="font-medium flex items-center gap-2">
                          {org.name}
                          <span className={`text-xs px-2 py-0.5 rounded ${getPlanBadgeColor(org.plan_code)}`}>
                            {org.plan_name || org.plan_code}
                          </span>
                        </h3>
                        <p className="text-sm text-muted-foreground">{org.contact_email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                            {getReasonLabel(org.deactivation_reason)}
                          </span>
                          {org.deactivated_at && (
                            <span className="text-xs text-muted-foreground">
                              Desde: {new Date(org.deactivated_at).toLocaleDateString('es-MX')}
                            </span>
                          )}
                        </div>
                        {org.deactivation_notes && (
                          <p className="text-xs text-muted-foreground mt-1 italic">
                            "{org.deactivation_notes}"
                          </p>
                        )}
                      </div>
                    </div>
                    <Button 
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleReactivate(org)}
                      data-testid={`reactivate-${org.id}`}
                    >
                      <Power size={14} className="mr-1" />
                      Reactivar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Plans Overview */}
        <div className="bg-card border border-border rounded-sm p-4">
          <h2 className="font-heading text-lg mb-4">Planes Disponibles</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {plans.map(plan => (
              <div key={plan.code} className={`border rounded-sm p-4 ${plan.code === 'ENTERPRISE' ? 'border-purple-500/50 bg-purple-500/5' : plan.code === 'PRO' ? 'border-blue-500/50 bg-blue-500/5' : 'border-border'}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">{plan.name}</h3>
                  <span className="text-lg font-bold">
                    {plan.price_monthly > 0 ? `$${plan.price_monthly}/mes` : t('register.free')}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{plan.description}</p>
                <div className="flex flex-wrap gap-1">
                  {plan.features.map(feat => (
                    <span key={feat} className="text-xs bg-secondary px-2 py-0.5 rounded">
                      {features[feat]?.name || feat}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Active Organizations List */}
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <div className="bg-secondary/50 px-4 py-3 border-b border-border flex items-center justify-between">
            <h2 className="font-heading text-lg">Organizaciones Activas ({activeOrgs.length})</h2>
          </div>
          
          {activeOrgs.length === 0 ? (
            <div className="p-8 text-center">
              <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No hay organizaciones activas</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowAddDialog(true)}>
                <Plus size={16} className="mr-1" />
                Crear Primera Organización
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {activeOrgs.map(org => (
                <div key={org.id} className="p-4" data-testid={`org-${org.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium flex items-center gap-2">
                          {org.name}
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                            Activa
                          </span>
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {org.contact_email || 'Sin email'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {/* Plan Selector */}
                      <Select 
                        value={org.plan_code} 
                        onValueChange={(v) => handlePlanChange(org.id, v)}
                      >
                        <SelectTrigger className={`w-40 ${getPlanBadgeColor(org.plan_code)}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {plans.map(p => (
                            <SelectItem key={p.code} value={p.code}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Deactivate Button */}
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-400 border-red-400/50 hover:bg-red-500/10"
                        onClick={() => openDeactivateDialog(org)}
                        data-testid={`deactivate-${org.id}`}
                      >
                        <PowerOff size={14} className="mr-1" />
                        Desactivar
                      </Button>
                      
                      {/* Settings */}
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setSelectedOrg(selectedOrg?.id === org.id ? null : org)}
                      >
                        <Settings size={16} />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Features Grid - Expandable */}
                  {selectedOrg?.id === org.id && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <h4 className="text-sm font-medium mb-3">Features Personalizadas</h4>
                      <p className="text-xs text-muted-foreground mb-4">
                        Activa o desactiva features individualmente. Las features activadas aquí sobrescriben el plan.
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {Object.entries(features).map(([code, info]) => {
                          const isEnabled = org.effective_features?.[code] || false;
                          const isCustom = org.custom_features?.[code] !== undefined;
                          const IconComponent = FEATURE_ICONS[code] || Package;
                          
                          return (
                            <div 
                              key={code}
                              className={`p-3 rounded-sm border ${isEnabled ? 'bg-primary/10 border-primary/30' : 'bg-secondary/30 border-border'} ${isCustom ? 'ring-1 ring-yellow-500/50' : ''}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <IconComponent size={16} className={isEnabled ? 'text-primary' : 'text-muted-foreground'} />
                                <Switch
                                  checked={isEnabled}
                                  onCheckedChange={() => handleFeatureToggle(org.id, code, isEnabled)}
                                  data-testid={`toggle-${code}-${org.id}`}
                                />
                              </div>
                              <div className="text-xs font-medium truncate">{info.name}</div>
                              {isCustom && (
                                <div className="text-xs text-yellow-400 mt-1">Personalizado</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Quick Features View */}
                  {selectedOrg?.id !== org.id && (
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(org.effective_features || {}).map(([code, enabled]) => {
                        if (!enabled) return null;
                        const IconComponent = FEATURE_ICONS[code] || Package;
                        return (
                          <span 
                            key={code}
                            className="inline-flex items-center gap-1 text-xs bg-secondary px-2 py-0.5 rounded"
                            title={features[code]?.name || code}
                          >
                            <IconComponent size={10} />
                            {features[code]?.name || code}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Organization Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nueva Organización</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Nombre de la Organización *</Label>
                <Input
                  value={newOrgForm.name}
                  onChange={(e) => setNewOrgForm({ ...newOrgForm, name: e.target.value })}
                  placeholder="Ej: Importadora ABC"
                  className="mt-1"
                  data-testid="org-name-input"
                />
              </div>
              
              <div>
                <Label>Plan Inicial</Label>
                <Select 
                  value={newOrgForm.plan_code} 
                  onValueChange={(v) => setNewOrgForm({ ...newOrgForm, plan_code: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map(p => (
                      <SelectItem key={p.code} value={p.code}>
                        {p.name} - {p.price_monthly > 0 ? `$${p.price_monthly}/mes` : t('register.free')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email de Contacto</Label>
                  <Input
                    type="email"
                    value={newOrgForm.contact_email}
                    onChange={(e) => setNewOrgForm({ ...newOrgForm, contact_email: e.target.value })}
                    placeholder="contacto@empresa.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <Input
                    value={newOrgForm.contact_phone}
                    onChange={(e) => setNewOrgForm({ ...newOrgForm, contact_phone: e.target.value })}
                    placeholder="+52 55 1234 5678"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreateOrg} data-testid="create-org-btn">
                  Crear Organización
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Deactivate Organization Dialog */}
        <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-400">
                <AlertTriangle size={20} />
                Desactivar Organización
              </DialogTitle>
              <DialogDescription>
                Esta acción desactivará la organización y todos sus usuarios. Los datos se conservarán.
              </DialogDescription>
            </DialogHeader>
            
            {orgToDeactivate && (
              <div className="space-y-4">
                <div className="bg-secondary/50 rounded-sm p-3">
                  <p className="font-medium">{orgToDeactivate.name}</p>
                  <p className="text-sm text-muted-foreground">{orgToDeactivate.contact_email}</p>
                </div>
                
                <div>
                  <Label>Motivo de Desactivación *</Label>
                  <Select 
                    value={deactivateForm.reason} 
                    onValueChange={(v) => setDeactivateForm({ ...deactivateForm, reason: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getDeactivationReasons(t).map(r => (
                        <SelectItem key={r.code} value={r.code}>
                          <div className="flex flex-col">
                            <span>{r.label}</span>
                            <span className="text-xs text-muted-foreground">{r.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Notas Adicionales (opcional)</Label>
                  <Textarea
                    value={deactivateForm.notes}
                    onChange={(e) => setDeactivateForm({ ...deactivateForm, notes: e.target.value })}
                    placeholder="Ej: Se contactó al cliente el día X, no respondió..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
                
                <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-3 text-sm">
                  <p className="text-red-400 font-medium mb-1">Consecuencias:</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• Los usuarios de esta organización no podrán acceder al sistema</li>
                    <li>• Los datos existentes se conservan y estarán disponibles al reactivar</li>
                    <li>• Esta acción se registra en el historial de auditoría</li>
                  </ul>
                </div>
                
                <div className="flex justify-end gap-3 pt-4 border-t border-border">
                  <Button variant="outline" onClick={() => setShowDeactivateDialog(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={handleDeactivate}
                    data-testid="confirm-deactivate-btn"
                  >
                    <PowerOff size={14} className="mr-1" />
                    Desactivar Cuenta
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        </>
        )}
      </div>
    </DashboardLayout>
  );
}
